import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ClientLogo from '@/components/ClientLogo';
import Navigation from '@/sections/Navigation';
import Footer from '@/sections/Footer';

interface Client {
  key: number;
  title: string;
  role: string;
  image: string;
  link: string;
  whatWeDid: string;
}

// Helper function to create URL-friendly name
const createSlug = (name: string): string => {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

const ClientDetail = () => {
  const { clientName } = useParams<{ clientName: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/clients/clients.json')
      .then((res) => res.json())
      .then((data: Client[]) => {
        const foundClient = data.find((c) => createSlug(c.title) === clientName);
        if (foundClient) {
          setClient(foundClient);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load clients:', err);
        setLoading(false);
      });
  }, [clientName]);


  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-slate-400">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Client not found</h1>
            <button
              onClick={() => navigate('/')}
              className="text-kmg-primary hover:underline"
            >
              Go back home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-12 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Vectoral System</span>
        </button>

        {/* Hero Section */}
        <div className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 mb-8">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <ClientLogo image={client.image} title={client.title} size="lg" />
              <div className="mt-4">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-3">
                  {client.title}
                </h1>
                <p className="text-xl text-slate-600 font-medium mb-2">
                  {client.role}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-4 items-center lg:items-start mt-4 lg:mt-6">
              <p className="text-base text-slate-500 italic leading-relaxed text-center lg:text-left max-w-xl">
                {client.title === 'Spikeball' && 'A backyard game turned global sports phenomenon. Featured on Shark Tank and ESPN, made the Inc. 500 list twice, and sold nearly $50 million worth of equipment in just a decade.'}
                {client.title === 'Mobispaw' && "Montreal's top-rated mobile pet grooming service. Brings the pet salon to your doorstep in a high-tech grooming van, ranking among the top pet groomers in Canada."}
                {client.title === 'The National Fix' && 'A full-service facilities maintenance powerhouse keeping thousands of client locations running smoothly. Handles nearly 8,000 facilities with 24/7 "Anyplace, Anytime, Anywhere" service.'}
                {client.title === 'Priveda Capital' && 'A fast-growing real estate investment firm that launched in 2024 and already amassed a portfolio of about 2,000 apartment units across Eastern Canada, including a $76.5 million acquisition.'}
                {client.title === 'Levine Bros' && 'A Montreal institution with over a century of service. Founded in 1922, this fourth-generation family business grew from a two-person operation into a fleet of 22 service trucks.'}
                {client.title === 'Customaps' && 'An innovative Montreal-born platform redefining map making. Built "the most complete website dedicated to maps", empowering users to design custom map posters, wallpapers, and digital maps.'}
                {client.title === 'CYG Finance' && 'A tech-driven accounting firm that acts as the outsourced finance department for 50+ companies, handling everything from bookkeeping and payroll to tax filings through a secure client portal.'}
                {client.title === 'Chatzy' && 'Chatzy processes millions of conversations through AI-powered automation'}
                {client.title === 'Viridia Capital' && 'Viridia Capital manages portfolios for high-net-worth clients'}
                {client.title === 'smarTAB' && 'smarTAB optimizes building performance across commercial facilities'}
                {!['Spikeball', 'Mobispaw', 'The National Fix', 'Priveda Capital', 'Levine Bros', 'Customaps', 'CYG Finance', 'Chatzy', 'Viridia Capital', 'smarTAB'].includes(client.title) && `${client.title} leverages technology to transform their operations`}
              </p>
            </div>
          </div>
        </div>

        {/* What We Did */}
        <div className="mb-16 p-8 bg-gradient-to-br from-kmg-primary/5 via-kmg-accent/5 to-kmg-primary/5 rounded-2xl border-2 border-kmg-primary/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-kmg-primary to-kmg-accent rounded-full"></div>
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
              <span className="text-kmg-primary">What we did</span>
            </h2>
          </div>
          <p className="text-lg text-slate-700 leading-relaxed max-w-4xl">
            {client.title === 'Mobispaw'
              ? "Through the KMG Vectoral System, we built Mobispaw's internal operations platform and client booking system. It centralizes booking, scheduling, staff coordination, payments, client records, and communication in one place. Clients can book online, get reminders, and follow pet updates, while the team manages pet profiles, staff schedules, and automated workflows. The system reduces manual work, minimizes missed appointments, and supports growth with review boosters and automated marketing tools."
              : client.title === 'Spikeball'
                ? "Through the KMG Vectoral System, we built Spikeball's mobile app and internal systems to increase community involvement and streamline operations. The app powers community engagement with events, play updates, and participation tracking, while collecting valuable activity data. That data feeds an internal dashboard where teams can process insights, monitor growth, and turn community behavior into clear, actionable decisions."
                : client.whatWeDid.charAt(0).toLowerCase() + client.whatWeDid.slice(1)}
          </p>
        </div>

      </main>
      <Footer />
    </div>
  );
};

export default ClientDetail;
